import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { default as LiveLegend } from "views/Statistics/SubscriberDetail/DPIAnalyticsLiveLegend";
import { debounce } from "common/api";
import NVLineChartLive from "common/NVLineChartLive";
import { formatDate } from "views/Statistics/SubscriberDetail/livemetrics.utils";
import {
  Title,
  CursorLine,
  LegendContainer,
  fieldTotal,
  field80Percentile,
  fieldMedian,
  fieldMin,
} from "./utils";
import { TooltipExtra } from "./Tooltip";
import NumberInput from "./NumberInput";

const MaxYWrapper = styled.div`
  margin-right: 20px;
  max-width: 200px;
  margin-left: ${(props) => props.marginLeft}px;
  @media (max-width: 500px) {
    width: 100%;
    max-width: 100%;
    margin-left: 0;
  }
`;

const ExtraChartDiv = styled.div`
  position: relative;
  .cancel-this-z-index {
    z-index: 0 !important; 
  }
`;

const ExtraChartContainer = styled.div`
  width: 50%;

  @media (max-width: 900px) {
    width: 100%;
  }
  .border {
    position: absolute;

    &.top {
      width: 100%;
      height: 55px;
      min-height: 55px;
      top: -50px;
      left: 0;
    }
    &.right {
      width: 45px;
      height: 100%;
      top: 0;
      right: 5px;
    }

    &.bottom {
      min-height: 70px;
      width: 100%;
      height: 30px;
      bottom: -40px;
      left: 0;
    }

    &.left {
      width: 50px;
      height: 100%;
      top: 0;
      left: 20px;
    }
  }

  .extraCharts {
    .nv-interactive {
      display: none !important;
    }
    .nv-scatterWrap {
      display: none;
    }
    .total.nv-group {
      stroke-width: 4 !important;

      path {
        stroke-width: 4 !important;
      }
    }
  }
`;

const getYAxisFormat = (type, max, maxValue) => {
  if (type.endsWith("rtx")) {
    return d3.format(".3f");
  }
  if (max > 1 || maxValue > 1) {
    return d3.format(".1f");
  }
  return d3.format(".3f");
};

const getTooltipX = (mouseEvent, rangeExtra, rangeMain) => {
  if (!mouseEvent) {
    return null;
  }
  if (mouseEvent.mouseXExtra !== null) {
    return mouseEvent.mouseXExtra;
  }

  return rangeMain > 0 ? mouseEvent.mouseXMain * (rangeExtra / rangeMain) : 0;
};

const getPlotSelectionNew = (plotSelection, isSpecialPercentile) => {
  if (!isSpecialPercentile) {
    return plotSelection;
  }

  const selectionWithoutTotal = plotSelection.filter(
    (name) => name !== "total"
  );
  return ["rttmin", "rtt80", "rttmed", ...selectionWithoutTotal];
};

const ExtraChart = ({
  type,
  items,
  margin,
  onMouseEvent,
  mouseEvent,
  setRangeExtra,
  rangeExtra,
  rangeMain,
  hoverderTime,
  currentData,
  width,
  fieldsMap,
  mouseXExtra,
  openDashboard,
  output,
  renderRate,
  max,
  plotSelection,
  isSpecialPercentile,
}) => {
  const [filterFields, setFilterFields] = useState(new Set());
  const [maxValue, setMaxValue] = useState("");
  const [filteredFields, setFilteredFields] = useState([]);
  const unit = type.endsWith("rtt") ? "ms" : "%";
  const unitLong = type.endsWith("rtt") ? "milliseconds" : "%";
  const linkName = type.startsWith("down") ? "Downlink" : "Uplink";

  function removeTooltip() {
    if (mouseEvent) {
      onMouseEvent(null);
    }
  }

  const plotSelectionNew = getPlotSelectionNew(
    plotSelection,
    isSpecialPercentile
  );

  const fields = fieldsMap
    ? plotSelectionNew.reduce((acc, name) => {
        if (fieldsMap[name]) {
          const { color } = fieldsMap[name];
          acc.push({ color, name, label: name });
        }
        return acc;
      }, [])
    : null;

  useEffect(() => {
    if (plotSelectionNew) {
      setFilterFields(new Set(plotSelectionNew));
    }
  }, [JSON.stringify([plotSelectionNew])]);

  useEffect(() => {
    if (filterFields) {
      setFilteredFields(fields.filter((field) => filterFields.has(field.name)));
    }
  }, [JSON.stringify([...filterFields])]);

  const title = type.endsWith("rtt")
    ? `${linkName} Latency`
    : `${linkName} Retransmissions`;
  const maxTitle = type.endsWith("rtt") ? `Max Latency` : `Max Retransmissions`;

  const createMouseEvent = (e) => {
    const newEvent = {
      ...e,
      rangeExtra,
      mouseXExtra: e.mouseX,
      mouseXMain: null,
    };
    onMouseEvent(newEvent);
  };

  const handleReset = () => {
    setMaxValue("");
  };

  const fixedYRange = maxValue !== "" ? [0, maxValue] : [0, null];
  const yAxisFormat = getYAxisFormat(type, max, maxValue);

  const sortedFields = useMemo(() => {
    if (!fields) {
      return [];
    }
    const { rest, otherFields } = fields
      .slice(isSpecialPercentile ? 3 : 1, fields.length)
      .reduce(
        (acc, value) => {
          if (value.name === "rest") {
            acc.rest = value;
          } else {
            acc.otherFields.push(value);
          }
          return acc;
        },
        { rest: undefined, otherFields: [] }
      );
    return [
      ...(isSpecialPercentile
        ? [field80Percentile, fieldMedian, fieldMin]
        : [fieldTotal]),
      ...otherFields.sort((a, b) => {
        return a.name.localeCompare(b.name);
      }),
      ...(rest !== undefined ? [rest] : []),
    ];
  }, [JSON.stringify(fields)]);

  return (
    <ExtraChartContainer>
      <Title className="chart-title">{title}</Title>
      <ExtraChartDiv>
        {items.length > 0 ? (
          <LegendContainer>
            <LiveLegend
              fields={sortedFields}
              onChange={(filter) => setFilterFields(filter)}
              value={filterFields}
              rest={false}
              onContextMenu={(name) => openDashboard(name, output)}
              maxWidthField="240"
              hasNameChange={true}
            />
          </LegendContainer>
        ) : null}
        <NVLineChartLive
          className="extraCharts"
          items={items}
          fields={filteredFields}
          yAxisUnits={unitLong}
          margin={margin}
          xAxisFormat={d3.timeFormat("%H:%M:%S")}
          yAxisFormat={yAxisFormat}
          minHeight="276px"
          onHighlight={(e) => {
            if (e) {
              debounce(createMouseEvent(e), 2000);
            }
          }}
          onRange={(rangeNew) => {
            if (rangeNew !== rangeExtra) {
              setRangeExtra(rangeNew);
            }
          }}
          fixedYRange={fixedYRange}
          addSerieNameToClass={true}
          renderRate={renderRate}
        />
        {mouseEvent && (
          <>
            <div
              className="border top"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
            <div
              className="border right"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
            <div
              className="border bottom"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
            <div
              className="border left"
              onMouseEnter={removeTooltip}
              onMouseLeave={removeTooltip}
            ></div>
          </>
        )}
        {mouseEvent && hoverderTime && mouseXExtra && filterFields.size > 0 && (
          <CursorLine
            style={{
              left: mouseXExtra + margin.left - 4,
              height: "242px",
              bottom: margin.bottom,
            }}
          />
        )}
        {mouseEvent && hoverderTime && filterFields.size > 0 && (
          <TooltipExtra
            dataPoint={currentData}
            mouseX={getTooltipX(mouseEvent, rangeExtra, rangeMain)}
            mouseY={mouseEvent.mouseY}
            title={formatDate(hoverderTime)}
            mouseEvent={mouseEvent}
            chartWidth={width}
            fieldsMap={fieldsMap}
            filterFields={filterFields}
            type={type}
            unit={unit}
          />
        )}
      </ExtraChartDiv>
      <MaxYWrapper marginLeft={margin.left}>
        <NumberInput
          name="limitY"
          label={maxTitle}
          className="input-control"
          initialValue={maxValue}
          step={0.001}
          min={0}
          onChange={(value) => setMaxValue(value)}
          onReset={handleReset}
        />
      </MaxYWrapper>
    </ExtraChartContainer>
  );
};

export default ExtraChart;
